import { useEffect, useState, useRef } from 'react';
import { Loader2, CheckCircle2, XCircle, AlertCircle } from 'lucide-react';
import { colors } from '../styles/colors';
import ModalPortal from './ModalPortal';

/**
 * ProgressModal - Modale affichant la progression d'une opération multi-étapes
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - État d'ouverture de la modale
 * @param {string} props.title - Titre de l'opération
 * @param {Array<{
 *   id: string,
 *   label: string,
 *   status: 'pending'|'in_progress'|'completed'|'error'|'disabled',
 *   subLabels?: string[],
 *   weight?: number
 * }>} props.steps - Étapes de l'opération
 * @param {function} props.onClose - Callback de fermeture (appelé uniquement quand terminé)
 * @param {string} props.errorMessage - Message d'erreur global (optionnel)
 * @param {boolean} props.canClose - Autoriser la fermeture manuelle (par défaut: true quand terminé)
 * @param {function} [props.onRetry] - Si fourni et qu'une étape est en erreur, affiche un
 *   bouton "Réessayer" à côté de "Fermer" pour reprendre sans tout relancer depuis le début.
 * @param {string} [props.numeroFacture] - Numéro de la facture en cours de traitement, affiché
 *   en sous-titre du header, indépendamment de l'état (pending/terminé/erreur) — contrairement
 *   au titre principal qui, lui, est remplacé par "Terminé"/"Erreur" une fois l'opération finie.
 */
function ProgressModal({
  isOpen,
  title = 'Opération en cours',
  steps = [],
  onClose,
  errorMessage = null,
  canClose = null,
  onRetry = null,
  numeroFacture = null
}) {
  const [animatedProgress, setAnimatedProgress] = useState(0);
  const [inProgressSubProgress, setInProgressSubProgress] = useState(0);
  const [currentSubLabelIndex, setCurrentSubLabelIndex] = useState(0);
  const animationRef = useRef(null);
  const subProgressRef = useRef(null);
  const subLabelIntervalRef = useRef(null);

  // Calculer la progression avec poids (les étapes désactivées ne comptent pas)
  const relevantSteps = steps.filter(s => s.status !== 'disabled');
  const totalWeight = relevantSteps.reduce((sum, s) => sum + (s.weight || 1), 0);

  const calculateProgress = () => {
    let progress = 0;
    for (const step of relevantSteps) {
      const stepWeight = step.weight || 1;
      const stepPercentage = (stepWeight / totalWeight) * 100;

      if (step.status === 'completed') {
        progress += stepPercentage;
      } else if (step.status === 'in_progress') {
        // Ajouter la progression partielle pour l'étape en cours
        progress += (stepPercentage * inProgressSubProgress) / 100;
      }
    }
    return progress;
  };

  const progress = calculateProgress();
  const completedSteps = relevantSteps.filter(s => s.status === 'completed').length;
  const errorSteps = steps.filter(s => s.status === 'error').length;
  const totalSteps = relevantSteps.length;
  const isComplete = completedSteps === totalSteps && totalSteps > 0;
  const hasError = errorSteps > 0 || errorMessage;

  // Trouver l'étape en cours
  const currentStep = steps.find(s => s.status === 'in_progress');

  // Animation de la sous-progression pour les étapes longues (comme PDF)
  useEffect(() => {
    if (currentStep && currentStep.weight > 1) {
      // Réinitialiser
      setInProgressSubProgress(0);
      setCurrentSubLabelIndex(0);

      // Animer progressivement vers ~90%
      const duration = (currentStep.weight || 1) * 3000;
      const startTime = Date.now();

      const animate = () => {
        const elapsed = Date.now() - startTime;
        const rawProgress = Math.min((elapsed / duration) * 90, 90);
        const easedProgress = 90 * (1 - Math.pow(1 - rawProgress / 90, 3));
        setInProgressSubProgress(easedProgress);

        if (elapsed < duration && currentStep.status === 'in_progress') {
          subProgressRef.current = requestAnimationFrame(animate);
        }
      };

      subProgressRef.current = requestAnimationFrame(animate);

      // Cycler à travers les sous-labels
      if (currentStep.subLabels && currentStep.subLabels.length > 1) {
        const intervalDuration = duration / currentStep.subLabels.length;
        let index = 0;

        subLabelIntervalRef.current = setInterval(() => {
          index++;
          if (index < currentStep.subLabels.length) {
            setCurrentSubLabelIndex(index);
          }
        }, intervalDuration);
      }

      return () => {
        if (subProgressRef.current) {
          cancelAnimationFrame(subProgressRef.current);
        }
        if (subLabelIntervalRef.current) {
          clearInterval(subLabelIntervalRef.current);
        }
      };
    } else {
      setInProgressSubProgress(0);
      setCurrentSubLabelIndex(0);
    }
  }, [currentStep]);

  // Quand une étape passe à completed, forcer 100%
  useEffect(() => {
    if (!currentStep) {
      setInProgressSubProgress(0);
      setCurrentSubLabelIndex(0);
    }
  }, [currentStep]);

  // Animation fluide de la barre de progression
  useEffect(() => {
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }

    const animate = () => {
      setAnimatedProgress(prev => {
        const diff = progress - prev;
        if (Math.abs(diff) < 0.5) return progress;
        return prev + diff * 0.15;
      });
      animationRef.current = requestAnimationFrame(animate);
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [progress]);

  // Déterminer si on peut fermer
  const allowClose = canClose !== null ? canClose : (isComplete || hasError);

  if (!isOpen) return null;

  const getStepIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle2 size={20} color={colors.status.success} />;
      case 'error':
        return <XCircle size={20} color={colors.status.error} />;
      case 'in_progress':
        return (
          <Loader2
            size={20}
            color={colors.primary.teal}
            style={{ animation: 'spin 1s linear infinite' }}
          />
        );
      case 'disabled':
        return (
          <div
            style={{
              width: 20,
              height: 20,
              borderRadius: '50%',
              border: `2px solid ${colors.secondary.lightGray}`,
              backgroundColor: colors.secondary.lightGray,
              opacity: 0.6
            }}
          />
        );
      default:
        return (
          <div
            style={{
              width: 20,
              height: 20,
              borderRadius: '50%',
              border: `2px solid ${colors.secondary.lightGray}`,
              backgroundColor: 'transparent'
            }}
          />
        );
    }
  };

  const getStepStyle = (status) => {
    switch (status) {
      case 'completed':
        return { color: colors.status.success, fontWeight: '500' };
      case 'error':
        return { color: colors.status.error, fontWeight: '500' };
      case 'in_progress':
        return { color: colors.primary.teal, fontWeight: '600' };
      case 'disabled':
        return { color: colors.text.secondary, opacity: 0.5, textDecoration: 'line-through' };
      default:
        return { color: colors.text.secondary };
    }
  };

  // Obtenir le sous-label actuel pour une étape
  const getCurrentSubLabel = (step) => {
    if (step.status !== 'in_progress') return null;
    if (step.subLabels && step.subLabels.length > 0) {
      return step.subLabels[currentSubLabelIndex] || step.subLabels[step.subLabels.length - 1];
    }
    return step.subLabel || null;
  };

  return (
    <ModalPortal>
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000,
          fontFamily: '"Noto Sans", sans-serif'
        }}
        onClick={allowClose ? onClose : undefined}
      >
        <div
          style={{
            backgroundColor: colors.text.white,
            borderRadius: '0.75rem',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.2)',
            width: '95%',
            maxWidth: '500px',
            overflow: 'hidden'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div
            style={{
              backgroundColor: hasError ? colors.status.error : (isComplete ? colors.status.success : colors.primary.teal),
              padding: '1.25rem 1.5rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              transition: 'background-color 0.3s ease'
            }}
          >
            {isComplete ? (
              <CheckCircle2 size={24} color="#fff" />
            ) : hasError ? (
              <AlertCircle size={24} color="#fff" />
            ) : (
              <Loader2 size={24} color="#fff" style={{ animation: 'spin 1s linear infinite' }} />
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.125rem' }}>
              <h2
                style={{
                  margin: 0,
                  fontSize: '1.125rem',
                  fontWeight: 'bold',
                  color: '#fff'
                }}
              >
                {isComplete ? 'Terminé' : (hasError ? 'Erreur' : title)}
              </h2>
              {numeroFacture && (
                <span
                  style={{
                    fontSize: '0.8125rem',
                    fontWeight: 500,
                    color: 'rgba(255, 255, 255, 0.85)'
                  }}
                >
                  Facture {numeroFacture}
                </span>
              )}
            </div>
          </div>

          {/* Progress bar */}
          <div
            style={{
              height: '6px',
              backgroundColor: colors.secondary.lightGray,
              position: 'relative',
              overflow: 'hidden'
            }}
          >
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                height: '100%',
                width: `${animatedProgress}%`,
                backgroundColor: hasError ? colors.status.error : (isComplete ? colors.status.success : colors.primary.gold),
                transition: 'background-color 0.3s ease'
              }}
            />
          </div>

          {/* Content */}
          <div style={{ padding: '1.5rem' }}>
            {/* Steps list */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {steps.map((step, index) => {
                const subLabel = getCurrentSubLabel(step);

                return (
                  <div
                    key={step.id || index}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '0.375rem'
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.75rem'
                      }}
                    >
                      {getStepIcon(step.status)}
                      <span style={{ fontSize: '0.9375rem', ...getStepStyle(step.status) }}>
                        {step.label}{step.status === 'disabled' ? ' (désactivé)' : ''}
                      </span>
                    </div>

                    {/* Sous-label détaillé pour l'étape en cours */}
                    {subLabel && (
                      <div
                        style={{
                          marginLeft: '2.25rem',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          minHeight: '1.25rem'
                        }}
                      >
                        <span
                          style={{
                            fontSize: '0.8125rem',
                            fontStyle: 'italic',
                            color: colors.text.secondary,
                            opacity: 0.9,
                            animation: 'fadeIn 0.3s ease-out'
                          }}
                        >
                          {subLabel}
                        </span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Error message */}
            {errorMessage && (
              <div
                style={{
                  marginTop: '1rem',
                  padding: '0.75rem 1rem',
                  backgroundColor: '#FEF2F2',
                  borderRadius: '0.5rem',
                  border: `1px solid ${colors.status.error}`,
                  color: colors.status.error,
                  fontSize: '0.875rem'
                }}
              >
                {errorMessage}
              </div>
            )}

            {/* Close / Retry buttons */}
            {allowClose && (
              <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1.5rem' }}>
                <button
                  onClick={onClose}
                  style={{
                    flex: 1,
                    padding: '0.75rem 1rem',
                    backgroundColor: hasError ? colors.secondary.lightGray : colors.primary.teal,
                    color: hasError ? colors.text.primary : '#fff',
                    border: 'none',
                    borderRadius: '0.5rem',
                    fontSize: '1rem',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'opacity 0.2s ease'
                  }}
                  onMouseEnter={(e) => e.target.style.opacity = '0.9'}
                  onMouseLeave={(e) => e.target.style.opacity = '1'}
                >
                  {hasError ? 'Fermer' : 'Continuer'}
                </button>
                {hasError && onRetry && (
                  <button
                    onClick={onRetry}
                    style={{
                      flex: 1,
                      padding: '0.75rem 1rem',
                      backgroundColor: colors.primary.teal,
                      color: '#fff',
                      border: 'none',
                      borderRadius: '0.5rem',
                      fontSize: '1rem',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'opacity 0.2s ease'
                    }}
                    onMouseEnter={(e) => e.target.style.opacity = '0.9'}
                    onMouseLeave={(e) => e.target.style.opacity = '1'}
                  >
                    Réessayer
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* CSS for animations */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-5px); }
          to { opacity: 0.9; transform: translateY(0); }
        }
      `}</style>
    </ModalPortal>
  );
}

export default ProgressModal;
